import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import h from "../../helpers";
import BouncingDots from "../loading/BouncingDots";
import buildSimpleColumns from "../../helpers/buildSimpleColumns";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHeader from "../material-components/Table/TableHeader";
import TableBody from "../material-components/Table/TableBody";
import Tooltip from "@material-ui/core/Tooltip";

/*
    This component does an ajax request and renders whatever comes back in a react table.
    Props:

    dataUrl: The url containing the data to render. (Required.)
    countUrl: A url that simply does a COUNT(*) on the info in the dataUrl and returns an integer of the count. (Required).

    header:  React node to display above the table.
    belowErrorMessage:  React node to display below the error message, useful for "Recompute" button or similiar.
*/

const tableReportAjaxTableDisplayProps = {
    // Passed In
    dataUrl: PropTypes.string.isRequired,
    countUrl: PropTypes.string.isRequired,
    header: PropTypes.node,
    footer: PropTypes.node,
    belowErrorMessage: PropTypes.node,
    showRecordCount: PropTypes.bool,
    downloadUrl: PropTypes.string,
    onPrimaryButtonDisabledFunc: PropTypes.func,
};

type TableReportAjaxTableDisplayProps = PropTypes.InferProps<typeof tableReportAjaxTableDisplayProps>;

const TableReportAjaxTableDisplay: React.FC<TableReportAjaxTableDisplayProps> = ({
    header,
    footer,
    belowErrorMessage,
    showRecordCount,
    downloadUrl,
    countUrl,
    dataUrl,
    onPrimaryButtonDisabledFunc,
}: TableReportAjaxTableDisplayProps) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isResultsLoaded, setIsResultsLoaded] = useState(false);
    const [records, setRecords] = useState<Array<any>>([]);
    const [recordCount, setRecordCount] = useState<number | null>(null);
    const [columns, setColumns] = useState([]);

    const applyFlowQAColumnHack = columns => {
        const shouldDoHack = dataUrl.includes("/FlowItemReports/QA/");

        if (!shouldDoHack || columns == null) {
            return columns;
        }

        // Always move "Node" to the front of the list
        // Generally we're getting the order of columns from the DB, but numeric-only columns seem to
        // introduce a problem.. probably need a more robust fix.
        const nodeColumns = columns.filter(x => x.id == "Node");
        if (nodeColumns.length == 1) {
            const nodeItem = nodeColumns[0];
            columns = columns.filter(x => x.id != "Node");
            columns.unshift(nodeItem);
        }

        return columns;
    };

    const formatEntry = entry =>
        entry != null && entry != undefined ? (
            <Tooltip title={entry}>
                <span>{entry}</span>
            </Tooltip>
        ) : (
            ""
        );

    useEffect(() => {
        if (!recordCount) {
            setLoading(true);
            fetch(countUrl, { credentials: "same-origin" })
                .then(h.checkStatus)
                .then(h.toJson)
                .then(recordCount => {
                    setRecordCount(recordCount as unknown as number);
                })
                .catch(error => {
                    setError(true);
                    setLoading(false);
                    h.errorSilent("Couldn't load TableReportAjaxTableDisplay[101]", error);
                });
        }
    }, [countUrl, recordCount]);

    useEffect(() => {
        if (recordCount && recordCount > 0 && records && records.length == 0) {
            fetch(dataUrl, { credentials: "same-origin" })
                .then(h.checkStatus)
                .then(h.toJson)
                .then(records => {
                    setRecords(records as unknown as Array<any>);
                    const columns = applyFlowQAColumnHack(buildSimpleColumns(records as unknown as Array<any>));
                    setColumns(columns);
                    setLoading(false);
                    setIsResultsLoaded(true);
                })
                .catch(error => {
                    if (onPrimaryButtonDisabledFunc) {
                        onPrimaryButtonDisabledFunc();
                    }
                    setError(true);
                    setLoading(false);
                    h.errorSilent("Couldn't load TableReportAjaxTableDisplay[102]", error);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataUrl, recordCount, records]);

    // Loading
    if (loading) {
        return (
            <div
                style={{
                    display: "block",
                    margin: "auto",
                    height: "300px",
                    width: "100px",
                    padding: "150px 0px 10px 0px",
                }}
            >
                <BouncingDots />
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div>
                <div className="row">
                    <div className="col-sm-6">
                        <div className="alert alert-danger m-t m-b-0">There was an error loading results.</div>
                        {belowErrorMessage}
                    </div>
                </div>
            </div>
        );
    }

    // BuildColumns doesn't run immediately after loading is set to false - prevent crash
    if (!columns || columns.length == 0) {
        return null;
    }

    // Set up table data and headers
    let tableHeaders: Array<any> = [];
    Object.values(columns).map((column: { id: any; Header: any }) => {
        tableHeaders.push([column.id, column.Header, "mdDown", "center"]);
    });

    let tableData: Array<any> = [];
    let tableEntry: any;
    Object.values(records).map((record, key) => {
        tableEntry = [];
        tableHeaders.map(header => {
            let entry = formatEntry(record[header[0]]);
            let row: Array<any> = [header[0] + "-" + key, entry, "mdDown", "center"];
            tableEntry.push(row);
        });
        tableData.push(tableEntry);
    });

    const emptyMessage: Array<string> = ["EmptyMessage, This table is empty", "mdDown", "center"];

    return (
        <div>
            {records.length > 0 && (
                <div>
                    {header}
                    <div className="m-t">
                        {
                            <Paper style={{ maxHeight: 340, width: "100%", overflow: "auto" }}>
                                <Table stickyHeader style={{ marginTop: 0 }}>
                                    <TableHeader headers={tableHeaders} ignoreHeaders={tableHeaders} />
                                    <TableBody
                                        data={tableData}
                                        page={0}
                                        rowsPerPage={tableData.length}
                                        emptyMessage={emptyMessage}
                                    />
                                </Table>
                            </Paper>
                        }
                    </div>
                    {showRecordCount && (
                        <div className="sub-table-label text-right m-t-xs">
                            Found {recordCount ? recordCount.toLocaleString() : "0"} total records.
                        </div>
                    )}

                    {downloadUrl && (
                        <div className="m-t-xs text-right">
                            <a
                                className="btn btn-sm btn-purple"
                                href={downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Download Report
                            </a>
                        </div>
                    )}
                    {footer}
                </div>
            )}
            {recordCount == 0 && isResultsLoaded && (
                <div className="row">
                    <div className="col-sm-6">
                        <div className="alert alert-info m-t m-b-0">No results matched your criteria.</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableReportAjaxTableDisplay;
